// DraggableRadiusMap.tsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  GoogleMap,
  Circle,
  OverlayViewF,
  MARKER_LAYER,
  FLOAT_PANE,
  OVERLAY_MOUSE_TARGET,
  InfoWindow
} from "@react-google-maps/api";
import { motion, AnimatePresence } from "framer-motion";
import useResponsive from "hooks/useResponsive";
import pinIcon from "assets/pin.png";
import { Link } from "react-router-dom";
import { ImageElement } from "elements/image/Image";
import { Toast } from "elements/toast/Toast";

interface DraggableRadiusMapProps {
  onCircleChange?: (info: { lat: number; lng: number; radius: number }) => void;
  onZoomChange?: (zoom: number) => void;
  initialCenter?: { lat: number; lng: number };
  initialRadius?: number;
  initialZoom?: number;
  style?: React.CSSProperties;
  allLocationsData: any;
  debouncedSearch: string;
}

const defaultCenter = { lat: 37.7749, lng: -122.4194 };
const defaultRadius = 3000;

// Bounds for the radius created by dragging the radius icon onto the map.
const DROP_RADIUS_MIN = 100; // ~330 ft
const DROP_RADIUS_MAX = 50000; // ~31 mi

// One-time coach mark for the dual-purpose radius button. Deliberately its
// own localStorage key (NOT inside "filteredMap") so "Clear All" filters
// can't resurrect it. Device-scoped and never cleared — the map is visible
// to signed-out users too, so tying it to auth would re-show it to people
// who already dismissed it.
const RADIUS_DRAG_HINT_KEY = "radiusDragHintSeen";

const BRAND_COLOR = "#B8722A";

// Clean, light map skin (soft blue water, bright parks, white roads) so the
// dark navy pins and their name labels are the visual focus. POI/transit
// labels are hidden — the salon pins are the points of interest.
const mapStyles: google.maps.MapTypeStyle[] = [
  { elementType: "geometry", stylers: [{ color: "#f7f5f0" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#7c828d" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
  {
    featureType: "poi",
    elementType: "labels",
    stylers: [{ visibility: "off" }]
  },
  {
    featureType: "poi",
    elementType: "geometry",
    stylers: [{ color: "#eceae4" }]
  },
  {
    featureType: "poi.park",
    elementType: "geometry",
    stylers: [{ color: "#b7e2a1" }]
  },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#ffffff" }]
  },
  {
    featureType: "road",
    elementType: "geometry.stroke",
    stylers: [{ visibility: "off" }]
  },
  {
    featureType: "road",
    elementType: "labels.text.fill",
    stylers: [{ color: "#9aa0ab" }]
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#a9d7f4" }]
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#7ba7c9" }]
  }
];

const formatRadius = (meters: number) => {
  const miles = meters / 1609.344;
  return miles >= 0.1
    ? `${miles.toFixed(1)} mi`
    : `${Math.round(meters * 3.28084)} ft`;
};

// Bounds that frame a circle of the given center/radius (in meters).
const getCircleBounds = (
  center: { lat: number; lng: number },
  radius: number
): google.maps.LatLngBounds => {
  const bounds = new google.maps.LatLngBounds();
  bounds.extend(center);

  const earthRadius = 6378137; // Earth’s radius in meters
  const latOffset = (radius / earthRadius) * (180 / Math.PI);
  const lngOffset =
    (radius / (earthRadius * Math.cos((Math.PI * center.lat) / 180))) *
    (180 / Math.PI);

  bounds.extend(
    new google.maps.LatLng(center.lat - latOffset, center.lng - lngOffset)
  );
  bounds.extend(
    new google.maps.LatLng(center.lat + latOffset, center.lng + lngOffset)
  );
  return bounds;
};

const DraggableRadiusMap: React.FC<DraggableRadiusMapProps> = ({
  onCircleChange,
  onZoomChange,
  initialCenter,
  initialRadius = defaultRadius,
  initialZoom,
  style,
  allLocationsData,
  debouncedSearch
}) => {
  // The Maps API is loaded once at the app root (App.tsx useLoadScript) and
  // App doesn't render children until it's ready. Loading it again here with
  // a second useLoadScript injected the API twice ("You have included the
  // Google Maps JavaScript API multiple times") and caused random crashes
  // inside Maps internals on WebKit/Safari.
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const { isMobile } = useResponsive();

  const [center, setCenter] = useState(initialCenter || defaultCenter);
  const [radius, setRadius] = useState(initialRadius);
  const [selectedLocation, setSelectedLocation] = useState<any>(null);
  const [hoveredLocation, setHoveredLocation] = useState<any>(null);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  // Pins are held back until the map settles after its initial fitBounds;
  // mounting them mid-pan makes the drop animation play while invisible.
  const [pinsReady, setPinsReady] = useState<boolean>(false);
  const [showDragHint, setShowDragHint] = useState<boolean>(
    () => !localStorage.getItem(RADIUS_DRAG_HINT_KEY)
  );

  // No auto-dismiss: the hint stays until the user closes it (tap on the
  // bubble) or uses the button (click / drag).
  const dismissDragHint = useCallback(() => {
    setShowDragHint(false);
    localStorage.setItem(RADIUS_DRAG_HINT_KEY, "true");
  }, []);

  const circleRef = useRef<google.maps.Circle | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  // Camera position is intentionally NOT bound to the circle-center state:
  // deliberate camera moves are all imperative (fitBounds/panTo), and moving
  // the circle (drag or icon-drop) must not pan the map. The frozen initial
  // value only seeds the camera at mount.
  const initialMapCenter = useRef(initialCenter || defaultCenter).current;
  const recenterBtnRef = useRef<HTMLDivElement | null>(null);
  // Dragging the icon uses pointer capture, so releasing it anywhere still
  // fires a click on the button; this flag lets the click handler tell a
  // real click from a just-finished drag (which must not recenter).
  const radiusDragActiveRef = useRef(false);
  // Latest callback, readable from the [] onMapLoad closure below.
  const onZoomChangeRef = useRef(onZoomChange);
  useEffect(() => {
    onZoomChangeRef.current = onZoomChange;
  });

  const circleOptions = useMemo<google.maps.CircleOptions>(
    () => ({
      strokeColor: BRAND_COLOR,
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: BRAND_COLOR,
      fillOpacity: 0.2,
      editable: true,
      draggable: true
    }),
    []
  );

  const onCircleLoad = useCallback((circle: google.maps.Circle) => {
    circleRef.current = circle;
  }, []);

  // Gentle "breathing" pulse on the circle fill so the search area reads as
  // alive/interactive. Applied directly on the Circle instance (no React
  // re-renders); paused while the user is dragging it.
  useEffect(() => {
    // localStorage.removeItem(RADIUS_DRAG_HINT_KEY);
    if (isDragging) return;
    let raf: number;
    const tick = (t: number) => {
      circleRef.current?.setOptions({
        fillOpacity: 0.2 + 0.06 * Math.sin(t / 700)
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isDragging]);

  const validLocations = useMemo(
    () =>
      (allLocationsData?.locations || []).filter(
        (location: any) =>
          location.address_latitude != null &&
          location.address_longitude != null
      ),
    [allLocationsData]
  );

  const handleRecenterAndZoom = useCallback(() => {
    if (mapRef.current && circleRef.current) {
      mapRef.current.fitBounds(getCircleBounds(center, radius));
    }
  }, [center, radius]);

  // North edge of the circle — anchor point for the floating radius label.
  const radiusLabelPosition = useMemo(
    () => ({
      lat: center.lat + (radius / 6378137) * (180 / Math.PI),
      lng: center.lng
    }),
    [center, radius]
  );

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    setTimeout(() => {
      // With a saved zoom the map is already centered on the circle via the
      // center prop — restore the user's last zoom instead of fitting bounds.
      if (initialZoom != null) {
        map.setZoom(initialZoom);
      } else {
        handleRecenterAndZoom();
      }
    }, 0);
    google.maps.event.addListenerOnce(map, "idle", () => setPinsReady(true));
    map.addListener("zoom_changed", () => {
      const zoom = map.getZoom();
      if (zoom != null) {
        onZoomChangeRef.current?.(zoom);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCenterChanged = useCallback(
    (dragEnd?: string) => {
      if (isDragging && !dragEnd) return;
      if (!circleRef.current) return;
      const newCenter = circleRef.current.getCenter();
      if (!newCenter) return;

      const newLat = newCenter.lat();
      const newLng = newCenter.lng();

      if (newLat !== center.lat || newLng !== center.lng) {
        setCenter({ lat: newLat, lng: newLng });
      }
    },
    [center, isDragging]
  );

  const onRadiusChanged = useCallback(
    (dragEnd?: string) => {
      if (isDragging && !dragEnd) return;
      if (!circleRef.current) return;
      const newRadius = circleRef.current.getRadius();
      if (newRadius && newRadius !== radius) {
        setRadius(newRadius);
      }
    },
    [radius, isDragging]
  );

  const geocodeAddress = useCallback(async (input: string) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: input }, (results, status) => {
      if (status !== "OK" || !results?.[0]) {
        return;
      }
      if (status === "OK" && results?.[0]) {
        const result = results[0];
        const bounds = result.geometry.bounds || result.geometry.viewport;
        const center = {
          lat: result.geometry.location.lat(),
          lng: result.geometry.location.lng()
        };

        // Compute radius from bounds diagonal
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        const radiusMeters =
          window.google.maps.geometry.spherical.computeDistanceBetween(ne, sw) /
          2;

        setCenter(center);
        setRadius(radiusMeters);

        if (mapRef.current) {
          mapRef.current.fitBounds(bounds);
        }
      }
    });
  }, []);

  useEffect(() => {
    if (!initialCenter?.lat && !initialCenter?.lng && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const userLocation = { lat: latitude, lng: longitude };
          if (mapRef?.current) {
            setTimeout(() => {
              setRadius(700);
              setCenter(userLocation);
              mapRef?.current?.panTo(userLocation); // 👈 this is what was missing
              mapRef?.current?.setZoom(15);
            }, 100);
          }
        },
        (error) => {
          // eslint-disable-next-line
          console.log("Geolocation failed or denied:", error);
        },
        { enableHighAccuracy: true }
      );
    }
  }, [initialCenter]);

  useEffect(() => {
    if (debouncedSearch) {
      geocodeAddress(debouncedSearch);
    }
  }, [debouncedSearch, geocodeAddress]);

  useEffect(() => {
    onCircleChange?.({ lat: center.lat, lng: center.lng, radius });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center, radius]);

  useEffect(() => {
    if (!toastMsg) return;
    const timeout = setTimeout(() => setToastMsg(null), 4000);
    return () => clearTimeout(timeout);
  }, [toastMsg]);

  // Drop target for the draggable radius icon: place a new circle at the
  // dropped screen point. Screen -> lat/lng via linear interpolation over the
  // current map bounds (accurate enough at city zoom levels).
  const handleRadiusDrop = (clientX: number, clientY: number) => {
    const map = mapRef.current;
    if (!map) return;
    // Dropping the icon back on its own button cancels the gesture.
    const btnRect = recenterBtnRef.current?.getBoundingClientRect();
    if (
      btnRect &&
      clientX >= btnRect.left &&
      clientX <= btnRect.right &&
      clientY >= btnRect.top &&
      clientY <= btnRect.bottom
    ) {
      return;
    }
    const rect = map.getDiv().getBoundingClientRect();
    if (
      clientX < rect.left ||
      clientX > rect.right ||
      clientY < rect.top ||
      clientY > rect.bottom
    ) {
      return; // dropped outside the map — do nothing
    }
    const bounds = map.getBounds();
    if (!bounds) return;
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    const lng =
      sw.lng() + ((clientX - rect.left) / rect.width) * (ne.lng() - sw.lng());
    const lat =
      ne.lat() - ((clientY - rect.top) / rect.height) * (ne.lat() - sw.lat());

    // Size the circle to the view — a quarter of the smaller viewport span —
    // clamped to sane absolute bounds so extreme zoom levels can't produce
    // absurd radii. Zoom is left untouched.
    const spherical = google.maps.geometry.spherical;
    const verticalSpan = spherical.computeDistanceBetween(
      new google.maps.LatLng(sw.lat(), lng),
      new google.maps.LatLng(ne.lat(), lng)
    );
    const horizontalSpan = spherical.computeDistanceBetween(
      new google.maps.LatLng(lat, sw.lng()),
      new google.maps.LatLng(lat, ne.lng())
    );
    const fitRadius = Math.min(verticalSpan, horizontalSpan) / 4;
    setCenter({ lat, lng });
    setRadius(Math.min(Math.max(fitRadius, DROP_RADIUS_MIN), DROP_RADIUS_MAX));
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation || !navigator.permissions) return;

    navigator.permissions.query({ name: "geolocation" }).then((result) => {
      if (result.state === "denied") {
        setToastMsg(
          "Location access is blocked. Please enable it in your browser settings."
        );
      } else {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            const userLocation = { lat: latitude, lng: longitude };
            setCenter(userLocation);
            setRadius(defaultRadius);
            // Explicit camera move: with the camera no longer bound to the
            // circle center, fit the new radius so this both centers and
            // zooms regardless of the current zoom level.
            mapRef.current?.fitBounds(
              getCircleBounds(userLocation, defaultRadius)
            );
          },
          (error) => {
            // eslint-disable-next-line
            console.log("Geolocation error:", error);
            setToastMsg("We couldn't determine your location. Try again.");
          },
          { enableHighAccuracy: true }
        );
      }
    });
  };

  return (
    <div className="w-full h-full">
      <GoogleMap
        mapContainerStyle={{
          width: "100%",
          height: isMobile ? "400px" : "calc(100dvh - 196px)",
          margin: "8px 0px 0px 0px",
          ...style
        }}
        center={initialMapCenter}
        zoom={12}
        options={{
          disableDefaultUI: true,
          zoomControl: true,
          // Default (bottom-right) placement sits under the filtered-salons
          // panel; RIGHT_TOP stacks the +/- right below the fullscreen button.
          zoomControlOptions: {
            position: google.maps.ControlPosition.RIGHT_TOP
          },
          fullscreenControl: true,
          streetViewControl: false,
          gestureHandling: "greedy",
          styles: mapStyles
        }}
        onLoad={onMapLoad}
        // Safety net for the hover pill: a fast cursor exit can outrun the
        // pin's own mouseleave, leaving the pill stuck. Any mouse movement
        // over the map that isn't on a pin clears the hover.
        onMouseMove={(e: google.maps.MapMouseEvent) => {
          const target = (e?.domEvent?.target as HTMLElement) || null;
          if (target?.closest?.("[data-pin]")) return;
          setHoveredLocation((prev: any) => (prev ? null : prev));
        }}
      >
        <Circle
          center={center}
          radius={radius}
          onLoad={onCircleLoad}
          onCenterChanged={onCenterChanged}
          onRadiusChanged={onRadiusChanged}
          options={circleOptions}
          onDragStart={() => setIsDragging(true)}
          onDragEnd={() => {
            setIsDragging(false);
            onCenterChanged("dragEnd");
            onRadiusChanged("dragEnd");
          }}
        />
        {/* markerLayer keeps these labels below the InfoWindow (floatPane). */}
        <OverlayViewF
          position={radiusLabelPosition}
          mapPaneName={MARKER_LAYER}
          getPixelPositionOffset={(width, height) => ({
            x: -width / 2,
            y: -height - 8
          })}
        >
          <div
            className="whitespace-nowrap rounded-full bg-white px-3 py-1
             text-[13px] font-[500] text-[#B8722A] pointer-events-none"
            style={{ boxShadow: "rgba(0, 0, 0, 0.25) 0px 1px 4px -1px" }}
          >
            {formatRadius(radius)} radius
          </div>
        </OverlayViewF>
        {/* HTML pins instead of the deprecated google.maps.Marker.
            overlayMouseTarget is the pane that receives DOM events. */}
        {pinsReady &&
          validLocations.map((location: any) => (
            <OverlayViewF
              key={location.id}
              position={{
                lat: location.address_latitude,
                lng: location.address_longitude
              }}
              mapPaneName={OVERLAY_MOUSE_TARGET}
              getPixelPositionOffset={(width, height) => ({
                x: -width / 2,
                y: -height
              })}
            >
              <motion.img
                initial={{ y: -250, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{
                  type: "spring",
                  stiffness: 400,
                  damping: 18,
                  delay: 0.5
                }}
                src={pinIcon}
                alt={location.name}
                width={25}
                height={30}
                className="cursor-pointer max-w-none"
                aria-hidden
                data-pin
                // Custom hover pill instead of the slow native title tooltip;
                // desktop only — on touch a tap goes straight to the modal.
                onMouseEnter={() => {
                  if (!isMobile) setHoveredLocation(location);
                }}
                onMouseLeave={() => setHoveredLocation(null)}
                onClick={() => {
                  setHoveredLocation(null);
                  setSelectedLocation(location);
                }}
              />
            </OverlayViewF>
          ))}
        {hoveredLocation && hoveredLocation.id !== selectedLocation?.id && (
          <OverlayViewF
            position={{
              lat: hoveredLocation.address_latitude,
              lng: hoveredLocation.address_longitude
            }}
            // floatPane sits above overlayMouseTarget (where the pins live);
            // in markerLayer the pill rendered underneath nearby pins.
            mapPaneName={FLOAT_PANE}
            getPixelPositionOffset={(width) => ({
              x: -width / 2,
              y: 6
            })}
          >
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.15 }}
              className="whitespace-nowrap rounded-full bg-white px-3 py-1
               text-[13px] font-[500] text-[#4a5261] pointer-events-none"
              style={{ boxShadow: "rgba(0, 0, 0, 0.25) 0px 1px 4px -1px" }}
            >
              {hoveredLocation.name}
            </motion.div>
          </OverlayViewF>
        )}
        {selectedLocation && (
          <InfoWindow
            position={{
              lat: selectedLocation?.address_latitude,
              lng: selectedLocation?.address_longitude
            }}
            // Lift the window above the 30px-tall pin so it stays visible.
            options={{ pixelOffset: new google.maps.Size(0, -34) }}
            onCloseClick={() => setSelectedLocation(null)} // Close InfoWindow on close
          >
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="flex gap-4 max-w-[375px]"
            >
              <div className="min-w-[100px] max-w-[100px] box-border">
                <ImageElement
                  className="h-full w-full object-cover"
                  src={selectedLocation.cover_photo_url}
                  alt={selectedLocation.name}
                  aspectRatio="100:140"
                />
              </div>
              <div className="flex flex-col gap-2">
                <div className="text-[20px] font-[500]">
                  {selectedLocation?.name}
                </div>
                <div className="text-[14px]">
                  {selectedLocation?.address_display}
                </div>
                <Link
                  to={`/company/${selectedLocation?.company?.slug}/location/${selectedLocation?.slug}`}
                >
                  <div className="text-tertiary text-[16px]">View Salon</div>
                </Link>
              </div>
            </motion.div>
          </InfoWindow>
        )}
      </GoogleMap>
      <AnimatePresence>
        {toastMsg && (
          <motion.div
            // x lives here, not in a Tailwind translate class — framer-motion
            // owns the transform property and would overwrite the class.
            initial={{ opacity: 0, y: -12, x: "-50%" }}
            animate={{ opacity: 1, y: 0, x: "-50%" }}
            exit={{ opacity: 0, y: -12, x: "-50%" }}
            className="absolute left-1/2 z-10 w-max max-w-[90%]"
            style={{ top: isMobile ? "200px" : "130px" }}
          >
            <Toast
              msg={toastMsg}
              type="warning"
              showIcon
              rounded
              onClose={() => setToastMsg(null)}
            />
          </motion.div>
        )}
      </AnimatePresence>
      {/* No whileHover scale here: a scale transform on this parent would
          multiply the dragged icon's translation and make it drift away from
          the cursor as the drag distance grows. */}
      <motion.div
        ref={recenterBtnRef}
        aria-hidden
        onClick={() => {
          if (radiusDragActiveRef.current) return;
          dismissDragHint();
          handleRecenterAndZoom();
        }}
        className="absolute left-3 w-10 h-10 flex justify-center items-center
         bg-white p-1 cursor-pointer rounded-[4px]"
        style={{
          top: isMobile ? "154px" : "82px",
          boxShadow: "rgba(0, 0, 0, 0.3) 0px 1px 4px -1px"
        }}
      >
        {/* Click the button = recenter on the radius. Drag the icon out and
            drop it on the map = move the radius there (see handleRadiusDrop).
            A drag ending elsewhere never fires the button's onClick, so the
            two gestures don't collide. */}
        <motion.div
          drag
          dragSnapToOrigin
          dragMomentum={false}
          whileDrag={{ scale: 1.4, zIndex: 30 }}
          whileHover={{ scale: 1.1 }}
          onDragStart={() => {
            radiusDragActiveRef.current = true;
            dismissDragHint();
          }}
          onDragEnd={(_event: any, info: any) => {
            handleRadiusDrop(
              info.point.x - window.scrollX,
              info.point.y - window.scrollY
            );
            // The synthesized click lands right after pointerup; keep the
            // flag up past it, then re-arm the button.
            setTimeout(() => {
              radiusDragActiveRef.current = false;
            }, 150);
          }}
          className="border-2 border-[#B8722A] bg-[rgba(184,114,42,0.2)]
           w-[30px] h-[30px] rounded-[50%] cursor-grab active:cursor-grabbing"
        />
      </motion.div>
      <AnimatePresence>
        {showDragHint && (
          <motion.div
            aria-hidden
            onClick={dismissDragHint}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -8 }}
            className="absolute z-20 w-[240px] rounded-lg bg-white px-3 py-2
             text-[13px] leading-snug text-[#302F2E] cursor-pointer"
            style={{
              left: "60px",
              top: isMobile ? "154px" : "82px",
              boxShadow: "rgba(0, 0, 0, 0.3) 0px 2px 8px -1px"
            }}
          >
            {/* Arrow pointing at the radius button. */}
            <div
              className="absolute -left-[5px] top-[13px] w-[10px] h-[10px]
               bg-white rotate-45"
            />
            Tap this button to jump back to your search area - or drag the
            circle onto the map to search somewhere else.
            <span className="block mt-1 font-[600] text-[#B8722A] text-right">
              Got it
            </span>
          </motion.div>
        )}
      </AnimatePresence>
      <motion.div
        aria-hidden
        onClick={handleUseMyLocation}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.92 }}
        className="absolute left-3 w-10 h-10 flex justify-center items-center
   bg-white p-1 cursor-pointer rounded-[4px]"
        style={{
          top: isMobile ? "200px" : "130px",
          boxShadow: "rgba(0, 0, 0, 0.3) 0px 1px 4px -1px"
        }}
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#B8722A"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <circle cx="12" cy="12" r="7" />
          <circle cx="12" cy="12" r="2.5" fill="#B8722A" stroke="none" />
          <line x1="12" y1="2" x2="12" y2="5" />
          <line x1="12" y1="19" x2="12" y2="22" />
          <line x1="2" y1="12" x2="5" y2="12" />
          <line x1="19" y1="12" x2="22" y2="12" />
        </svg>
      </motion.div>
    </div>
  );
};

export default DraggableRadiusMap;
